"use client";

import { Collapsible } from "@base-ui/react/collapsible";
import { NavArrowDown } from "iconoir-react";
import { useTranslations } from "next-intl";
import * as React from "react";

import { Input } from "@/components/ui/input";
import {
  amountFromSlider,
  PRICING_RANGES,
  sliderFromAmount,
} from "@/lib/pricing-calculator/defaults";
import type {
  MonthSummary,
  RepaymentFrequency,
  RepaymentStructure,
} from "@/lib/pricing-calculator/model";
import type { CalculatorState } from "@/lib/pricing-calculator/url-state";
import { cn } from "@/lib/utils";

import { useCalculatorEnvironment } from "./context";
import {
  Card,
  Field,
  NumberInput,
  SegmentedControl,
  SliderField,
  Switch,
} from "./controls";
import { SeasonalityEditor } from "./seasonality-editor";

interface InputsPanelProps {
  state: CalculatorState;
  months: readonly MonthSummary[];
  onChange: (patch: Partial<CalculatorState>) => void;
}

function GroupHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-ds-heading-h5 text-grey-950">{children}</h2>;
}

export function InputsPanel({ state, months, onChange }: InputsPanelProps) {
  const t = useTranslations("pages.pricing.calculator.inputs");
  const { format, currencySymbol } = useCalculatorEnvironment();
  const isCashAdvance = state.structure === "cash-advance";

  const structureOptions = [
    {
      value: "term-loan" as RepaymentStructure,
      label: t("structure.termLoan"),
      hint: t("structure.termLoanHint"),
    },
    {
      value: "cash-advance" as RepaymentStructure,
      label: t("structure.cashAdvance"),
      hint: t("structure.cashAdvanceHint"),
    },
  ];

  const frequencyOptions = [
    { value: "daily" as RepaymentFrequency, label: t("frequency.daily") },
    { value: "weekly" as RepaymentFrequency, label: t("frequency.weekly") },
  ];

  // Quarter-year marks: 3, 6, 9, 12, 15, 18 sit at even intervals across the
  // 3–18 track, so the labels read as months a founder plans in.
  const termTicks = React.useMemo(() => {
    const { min, max } = PRICING_RANGES.termMonths;
    const ticks: string[] = [];
    for (let month = min; month <= max; month += 3) ticks.push(String(month));
    return ticks;
  }, []);

  return (
    <Card
      className="flex flex-col gap-8"
      connectors="both"
      data-testid="pricing-inputs"
    >
      <section
        className="flex flex-col gap-6"
        aria-labelledby="pricing-inputs-financing"
      >
        <GroupHeading>
          <span id="pricing-inputs-financing">{t("title")}</span>
        </GroupHeading>

        <SegmentedControl
          aria-label={t("structureLabel")}
          size="lg"
          value={state.structure}
          options={structureOptions}
          onChange={(structure) => onChange({ structure })}
        />

        <SliderField
          id="pricing-amount"
          label={t("amountLabel")}
          value={state.amount}
          onChange={(amount) => onChange({ amount })}
          min={PRICING_RANGES.amount.min}
          max={PRICING_RANGES.amount.max}
          prefix={currencySymbol}
          toSlider={sliderFromAmount}
          fromSlider={amountFromSlider}
          formatValue={(value) => format.money(value)}
          inputWidthClassName="w-36"
        />

        <SliderField
          id="pricing-fee"
          label={t("feeLabel")}
          hint={t("feeHint")}
          value={state.feePercent}
          onChange={(feePercent) => onChange({ feePercent })}
          min={PRICING_RANGES.feePercent.min}
          max={PRICING_RANGES.feePercent.max}
          typedMax={PRICING_RANGES.feePercent.typedMax}
          step={PRICING_RANGES.feePercent.step}
          decimalScale={1}
          suffix="%"
          formatValue={(value) => format.percent(value, 1)}
          inputWidthClassName="w-24"
        />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Field id="pricing-start-date" label={t("startDateLabel")}>
            <Input
              id="pricing-start-date"
              type="date"
              value={state.startDate}
              min="2000-01-01"
              max="2100-12-31"
              onChange={(event) => {
                const value = event.target.value;
                if (value && !Number.isNaN(Date.parse(value))) {
                  onChange({ startDate: value });
                }
              }}
              className="tabular-nums"
            />
          </Field>

          <div className="flex flex-col gap-2">
            <span className="text-ds-body-sm text-grey-700">
              {t("frequencyLabel")}
            </span>
            <SegmentedControl
              aria-label={t("frequencyLabel")}
              value={state.frequency}
              options={frequencyOptions}
              onChange={(frequency) => onChange({ frequency })}
              className="h-11 w-full"
            />
          </div>
        </div>

        {!isCashAdvance && (
          <SliderField
            id="pricing-term"
            label={t("termLabel")}
            value={state.termMonths}
            onChange={(termMonths) => onChange({ termMonths })}
            min={PRICING_RANGES.termMonths.min}
            max={PRICING_RANGES.termMonths.max}
            step={PRICING_RANGES.termMonths.step}
            suffix={t("termUnit")}
            formatValue={(value) => t("termValue", { count: value })}
            ticks={termTicks}
            inputWidthClassName="w-32"
          />
        )}
      </section>

      {isCashAdvance && (
        <section
          className="border-grey-100 flex flex-col gap-6 border-t pt-8"
          aria-labelledby="pricing-inputs-revenue"
        >
          <GroupHeading>
            <span id="pricing-inputs-revenue">{t("revenueTitle")}</span>
          </GroupHeading>

          <SliderField
            id="pricing-share"
            label={t("shareLabel")}
            hint={t("shareHint", {
              frequency: t(`shareHintFrequency.${state.frequency}`),
            })}
            value={state.revenueSharePercent}
            onChange={(revenueSharePercent) =>
              onChange({ revenueSharePercent })
            }
            min={PRICING_RANGES.revenueSharePercent.min}
            max={PRICING_RANGES.revenueSharePercent.max}
            typedMin={PRICING_RANGES.revenueSharePercent.typedMin}
            typedMax={PRICING_RANGES.revenueSharePercent.typedMax}
            step={PRICING_RANGES.revenueSharePercent.step}
            suffix="%"
            formatValue={(value) => format.percent(value, 0)}
            inputWidthClassName="w-24"
          />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field id="pricing-revenue" label={t("revenueLabel")}>
              <NumberInput
                id="pricing-revenue"
                value={state.monthlyRevenue}
                onChange={(monthlyRevenue) => onChange({ monthlyRevenue })}
                min={PRICING_RANGES.monthlyRevenue.min}
                max={PRICING_RANGES.monthlyRevenue.max}
                prefix={currencySymbol}
              />
            </Field>
            <Field id="pricing-growth" label={t("growthLabel")}>
              <NumberInput
                id="pricing-growth"
                value={state.monthlyGrowthPercent}
                onChange={(monthlyGrowthPercent) =>
                  onChange({ monthlyGrowthPercent })
                }
                min={PRICING_RANGES.monthlyGrowthPercent.min}
                max={PRICING_RANGES.monthlyGrowthPercent.max}
                decimalScale={1}
                suffix="%"
              />
            </Field>
          </div>

          <Collapsible.Root className="flex flex-col gap-4">
            <Collapsible.Trigger className="group text-ds-body-sm text-grey-950 rounded-ds-full inline-flex w-fit items-center gap-2 font-medium focus-visible:shadow-[0_0_0_2px_var(--grey-0),0_0_0_4px_var(--pulse-700)] focus-visible:outline-none">
              <span className="border-grey-200 rounded-ds-full flex size-6 items-center justify-center border transition-transform duration-200 group-data-[panel-open]:rotate-180">
                <NavArrowDown className="size-3.5" aria-hidden />
              </span>
              {t("seasonalityToggle")}
            </Collapsible.Trigger>
            <Collapsible.Panel className="h-[var(--collapsible-panel-height)] overflow-hidden transition-[height] duration-200 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 motion-reduce:transition-none">
              <SeasonalityEditor
                months={months}
                overrides={state.revenueOverrides}
                onChange={(revenueOverrides) => onChange({ revenueOverrides })}
              />
            </Collapsible.Panel>
          </Collapsible.Root>

          <div
            className={cn(
              "rounded-ds-xl border p-4 transition-colors duration-200",
              state.capEnabled
                ? "border-pulse-200 bg-pulse-50"
                : "border-grey-100 bg-grey-50"
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <span
                  id="pricing-cap-title"
                  className="text-ds-body-md text-grey-950 font-medium"
                >
                  {t("capTitle")}
                </span>
                <span className="text-ds-body-xs text-grey-600">
                  {t("capHint")}
                </span>
              </div>
              <Switch
                aria-labelledby="pricing-cap-title"
                checked={state.capEnabled}
                onCheckedChange={(capEnabled) => onChange({ capEnabled })}
              />
            </div>
            {state.capEnabled && (
              <div className="mt-4">
                <Field id="pricing-cap" label={t("capLabel")}>
                  <NumberInput
                    id="pricing-cap"
                    value={state.monthlyCap}
                    onChange={(monthlyCap) => onChange({ monthlyCap })}
                    min={PRICING_RANGES.monthlyCap.min}
                    max={PRICING_RANGES.monthlyCap.max}
                    prefix={currencySymbol}
                  />
                </Field>
              </div>
            )}
          </div>
        </section>
      )}
    </Card>
  );
}
